import React from "react";

const base =
  "w-full rounded-lg border border-softgray bg-white px-4 py-3.5 text-charcoal shadow-sm placeholder:text-slate/60 focus:border-navy focus:outline-none focus:ring-2 focus:ring-navy/25 transition-colors";

export const Field = ({ label, required, hint, error, children, htmlFor }) => (
  <div className="flex flex-col gap-2">
    {label && (
      <label htmlFor={htmlFor} className="text-sm font-semibold text-charcoal">
        {label} {required && <span className="text-gold-deep">*</span>}
      </label>
    )}
    {children}
    {hint && !error && <span className="text-xs text-slate">{hint}</span>}
    {error && <span className="text-xs font-medium text-red-600" data-testid={`error-${htmlFor}`}>{error}</span>}
  </div>
);

export const Input = React.forwardRef(({ className = "", ...props }, ref) => (
  <input ref={ref} className={`${base} ${className}`} {...props} />
));
Input.displayName = "Input";

export const Textarea = ({ className = "", ...props }) => (
  <textarea className={`${base} min-h-[120px] resize-y ${className}`} {...props} />
);

export const Select = ({ className = "", children, ...props }) => (
  <select className={`${base} appearance-none bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 fill=%22none%22 stroke=%22%236B7280%22 stroke-width=%222%22 viewBox=%220 0 24 24%22><path d=%22M6 9l6 6 6-6%22/></svg>')] bg-[length:18px] bg-[right_14px_center] bg-no-repeat pr-10 ${className}`} {...props}>
    {children}
  </select>
);

export const Checkbox = ({ label, id, checked, onChange, error }) => (
  <div className="flex flex-col gap-1.5">
    <label htmlFor={id} className="flex items-start gap-3 cursor-pointer select-none">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        data-testid={id}
        className="mt-1 h-5 w-5 shrink-0 rounded border-softgray text-navy accent-navy focus:ring-2 focus:ring-navy/30"
      />
      <span className="text-sm text-charcoal/85 leading-relaxed">{label}</span>
    </label>
    {error && <span className="text-xs font-medium text-red-600 pl-8" data-testid={`error-${id}`}>{error}</span>}
  </div>
);

export const RadioPills = ({ options, value, onChange, name }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((opt) => {
      const active = value === opt;
      return (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          data-testid={`${name}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className={`rounded-full border px-5 py-2.5 text-sm font-semibold transition-colors ${
            active ? "border-navy bg-navy text-offwhite" : "border-softgray bg-white text-charcoal hover:border-navy"
          }`}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

export const SubmitButton = ({ children, loading, testid }) => (
  <button
    type="submit"
    disabled={loading}
    data-testid={testid}
    className="group inline-flex items-center justify-center gap-2 rounded-lg bg-navy px-8 py-4 text-sm font-semibold text-offwhite hover:bg-navy-deep disabled:opacity-60 transition-colors"
  >
    {loading ? "Sending…" : children}
  </button>
);
